import type { ResumeDraft, ResumeSectionId, TemplateId } from '../../lib/resume/types';
import { RESUME_SECTION_LABEL } from '../../lib/resume/types';

/**
 * A single-column, text-first resume preview.
 *
 * The markup is deliberately plain: real `h1`/`h2`/`h3` headings, ordered
 * paragraphs and lists, and no absolutely positioned blocks, columns of body
 * text, background images, or icon-only labels. Anything a parser can misread is
 * avoided here, and the three templates differ only in typography and spacing.
 */

interface Props {
  draft: ResumeDraft;
  template?: TemplateId;
  /** Rendered without chrome for on-screen preview. */
  className?: string;
}

type Style = {
  sheet: string;
  name: string;
  contact: string;
  sectionTitle: string;
  entryTitle: string;
  entryMeta: string;
  body: string;
  bullet: string;
  divider: string;
};

const STYLES: Record<TemplateId, Style> = {
  classic: {
    sheet: 'font-serif text-[13px] leading-relaxed',
    name: 'text-2xl font-bold tracking-tight',
    contact: 'text-[12px]',
    sectionTitle:
      'mt-5 border-b border-black/30 pb-1 text-[13px] font-bold uppercase tracking-wide',
    entryTitle: 'text-[13px] font-semibold',
    entryMeta: 'text-[12px] italic',
    body: 'text-[13px]',
    bullet: 'list-disc pl-5',
    divider: '',
  },
  modern: {
    sheet: 'font-sans text-[13px] leading-relaxed',
    name: 'text-2xl font-extrabold tracking-tight',
    contact: 'text-[12px]',
    sectionTitle:
      'mt-6 border-b-2 border-black pb-1 text-[12px] font-bold uppercase tracking-widest',
    entryTitle: 'text-[13px] font-bold',
    entryMeta: 'text-[12px]',
    body: 'text-[13px]',
    bullet: 'list-disc pl-5',
    divider: '',
  },
  compact: {
    sheet: 'font-sans text-[11.5px] leading-snug',
    name: 'text-xl font-bold tracking-tight',
    contact: 'text-[11px]',
    sectionTitle:
      'mt-3 border-b border-black/20 pb-0.5 text-[11px] font-bold uppercase tracking-wide',
    entryTitle: 'text-[11.5px] font-semibold',
    entryMeta: 'text-[11px]',
    body: 'text-[11.5px]',
    bullet: 'list-disc pl-4',
    divider: '',
  },
};


function splitList(values: string[]): string[] {
  return values.map((value) => value.trim()).filter((value) => value.length > 0);
}

export function ResumePreview({ draft, template, className = '' }: Props) {
  const active: TemplateId = template ?? draft.template;
  const style = STYLES[active];
  const contactLine = [
    draft.contact.location.value,
    draft.contact.phone.value,
    draft.contact.email.value,
  ].filter((value) => value.trim().length > 0);

  return (
    <article
      className={`mx-auto w-full max-w-[816px] rounded-sm bg-white px-8 py-9 text-black shadow-[0_1px_2px_rgba(11,13,23,0.08),0_20px_50px_-30px_rgba(41,49,93,0.5)] sm:px-11 sm:py-12 ${style.sheet} ${className}`}
      data-template={active}
      aria-label="Resume preview"
    >
      <header>
        <h1 className={`${style.name} mt-0`}>{draft.contact.fullName.value || 'Your name'}</h1>
        {draft.targetRole.trim().length > 0 && (
          <p className={`${style.entryMeta} mt-0.5`}>{draft.targetRole}</p>
        )}
        {contactLine.length > 0 && (
          <p className={`${style.contact} mt-1`}>{contactLine.join(' · ')}</p>
        )}
        {draft.contact.links.length > 0 && (
          <p className={`${style.contact} mt-0.5`}>
            {draft.contact.links
              .filter((link) => link.url.trim().length > 0)
              .map((link) => link.url)
              .join(' · ')}
          </p>
        )}
      </header>

      {draft.sectionOrder.map((section) => (
        <Section key={section} section={section} draft={draft} style={style} />
      ))}
    </article>
  );
}

function Section({ section, draft, style }: { section: ResumeSectionId; draft: ResumeDraft; style: Style }) {
  switch (section) {
    case 'summary': {
      if (draft.summary.value.trim().length === 0) return null;
      return (
        <section aria-labelledby={`resume-${section}`}>
          <h2 id={`resume-${section}`} className={style.sectionTitle}>
            {RESUME_SECTION_LABEL[section]}
          </h2>
          <p className={`${style.body} mt-1`}>{draft.summary.value}</p>
        </section>
      );
    }
    case 'experience': {
      if (draft.experience.length === 0) return null;
      return (
        <section aria-labelledby={`resume-${section}`}>
          <h2 id={`resume-${section}`} className={style.sectionTitle}>
            {RESUME_SECTION_LABEL[section]}
          </h2>
          {draft.experience.map((entry) => {
            const responsibilities = splitList(entry.responsibilities);
            const achievements = splitList(entry.achievements);
            const meta = [entry.organization, entry.location, entry.periodRaw]
              .filter((value) => value.trim().length > 0)
              .join(' · ');
            return (
              <div key={entry.id} className="mt-2">
                <h3 className={`${style.entryTitle} mt-0`}>{entry.role || 'Role'}</h3>
                {meta.length > 0 && <p className={`${style.entryMeta} mt-0`}>{meta}</p>}
                {(responsibilities.length > 0 || achievements.length > 0) && (
                  <ul className={`${style.bullet} mt-1`}>
                    {responsibilities.map((line, index) => (
                      <li key={`r-${entry.id}-${index}`}>{line}</li>
                    ))}
                    {achievements.map((line, index) => (
                      <li key={`a-${entry.id}-${index}`}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </section>
      );
    }
    case 'education': {
      if (draft.education.length === 0) return null;
      return (
        <section aria-labelledby={`resume-${section}`}>
          <h2 id={`resume-${section}`} className={style.sectionTitle}>
            {RESUME_SECTION_LABEL[section]}
          </h2>
          {draft.education.map((entry) => {
            const meta = [entry.institution, entry.periodRaw]
              .filter((value) => value.trim().length > 0)
              .join(' · ');
            return (
              <div key={entry.id} className="mt-2">
                <h3 className={`${style.entryTitle} mt-0`}>{entry.credential || 'Qualification'}</h3>
                {meta.length > 0 && <p className={`${style.entryMeta} mt-0`}>{meta}</p>}
                {entry.area.trim().length > 0 && <p className={`${style.body} mt-0.5`}>{entry.area}</p>}
              </div>
            );
          })}
        </section>
      );
    }
    case 'skills': {
      const skills = splitList(draft.skills.map((entry) => entry.name));
      if (skills.length === 0) return null;
      return (
        <section aria-labelledby={`resume-${section}`}>
          <h2 id={`resume-${section}`} className={style.sectionTitle}>
            {RESUME_SECTION_LABEL[section]}
          </h2>
          <p className={`${style.body} mt-1`}>{skills.join(', ')}</p>
        </section>
      );
    }
    case 'certifications': {
      if (draft.certifications.length === 0) return null;
      return (
        <section aria-labelledby={`resume-${section}`}>
          <h2 id={`resume-${section}`} className={style.sectionTitle}>
            {RESUME_SECTION_LABEL[section]}
          </h2>
          <ul className={`${style.bullet} mt-1`}>
            {draft.certifications.map((entry) => {
              const meta = [entry.issuer, entry.date].filter((value) => value.trim().length > 0).join(' · ');
              return (
                <li key={entry.id}>
                  {entry.name}
                  {meta.length > 0 ? ` — ${meta}` : ''}
                </li>
              );
            })}
          </ul>
        </section>
      );
    }
    case 'projects': {
      if (draft.projects.length === 0) return null;
      return (
        <section aria-labelledby={`resume-${section}`}>
          <h2 id={`resume-${section}`} className={style.sectionTitle}>
            {RESUME_SECTION_LABEL[section]}
          </h2>
          {draft.projects.map((entry) => (
            <div key={entry.id} className="mt-2">
              <h3 className={`${style.entryTitle} mt-0`}>{entry.name || 'Project'}</h3>
              {entry.url.trim().length > 0 && <p className={`${style.entryMeta} mt-0`}>{entry.url}</p>}
              {entry.description.trim().length > 0 && (
                <p className={`${style.body} mt-0.5`}>{entry.description}</p>
              )}
            </div>
          ))}
        </section>
      );
    }
  }
}
